import {
  DestinationEntityRecordStatus,
  ISourceEntityRecord,
  ITranslationMap,
  SkippedDestinationEntityRecord,
  type FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityJobRunOptions,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
  type ISource,
  type MergeConflictDestinationEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import { Media, Patient, Practice } from '@principle-theorem/principle-core';
import {
  type IMedia,
  type IPatient,
  type ITag,
} from '@principle-theorem/principle-core/interfaces';
import {
  FirestoreMigrate,
  PrimaryKey,
  WithPrimaryKey,
  errorNil,
  firstResult,
  getError,
  resolveSequentially,
  snapshotCombineLatest,
  toNamedDocument,
  toTimestamp,
  where,
  type DocumentReference,
  type INamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, first, groupBy, uniq } from 'lodash';
import { combineLatest, from, of, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { BaseDestinationEntity } from '../../../destination/base-destination-entity';
import { DestinationEntity } from '../../../destination/destination-entity';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import { getBucketFileCreatedAt } from '../../../destination/entities/patient-files';
import { PatientIdFilter } from '../../../destination/filters/patient-id-filter';
import { PracticeIdFilter } from '../../../destination/filters/practice-id-filter';
import {
  CopyFile,
  CopyFiles,
  getBucketStoragePath,
  type IStorageSyncResponse,
} from '../../../storage';
import { type TranslationMapHandler } from '../../../translation-map';
import { D4WPatient, PatientSourceEntity } from '../../source/entities/patient';
import {
  D4WPatientDocument,
  PATIENT_DOCUMENT_RESOURCE_TYPE,
  PatientDocumentSourceEntity,
} from '../../source/entities/patient-document';
import { D4WFileCategoryMappingHandler } from '../mappings/file-categories';
import { PatientDestinationEntity } from './patients';

export const PATIENT_FILE_RESOURCE_TYPE = 'patientFiles';

export const PATIENT_FILE_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: PATIENT_FILE_RESOURCE_TYPE,
    label: 'Patient Files',
    description: '',
  },
});

export const CLOUD_STORAGE_CREATED_AT_METADATA_KEY = 'goog-reserved-file-mtime';

export interface IPatientFileDestinationRecord {
  fileRefs: DocumentReference<IMedia>[];
}

export interface IPatientFileJobData {
  sourcePatient: IGetRecordResponse<D4WPatient>;
  files: IFileSummary[];
  fileCategories: WithPrimaryKey<ITranslationMap<ITag>>[];
  documentTag: WithRef<ITag>;
}

interface IFileSummary {
  patientName: string;
  patientId: string;
  fileName: string;
  folderPath: string;
  categoryName?: string;
}

export interface IPatientFileMigrationData {
  sourcePatientId: string;
  patientRef: DocumentReference<IPatient>;
  documents: IGetRecordResponse<D4WPatientDocument>[];
  files: IFileSummary[];
  fileCategories: WithPrimaryKey<ITranslationMap<ITag>>[];
}

export class PatientFileDestinationEntity extends BaseDestinationEntity<
  IPatientFileDestinationRecord,
  IPatientFileJobData,
  IPatientFileMigrationData
> {
  destinationEntity = PATIENT_FILE_DESTINATION_ENTITY;

  override filters = [
    new PracticeIdFilter<IPatientFileJobData>((jobData) =>
      jobData.sourcePatient.data.practice_id.toString()
    ),
    new PatientIdFilter<IPatientFileJobData>((jobData) =>
      this.sourceEntities.patients
        .getSourceRecordId(jobData.sourcePatient.data)
        .toString()
    ),
  ];

  sourceCountComparison = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    patientDocuments: new PatientDocumentSourceEntity(),
  };

  customMappings = {
    fileCategories: new D4WFileCategoryMappingHandler(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  sourceCountDataAccessor(
    data: IPatientFileJobData
  ): PrimaryKey<ISourceEntityRecord> {
    return data.sourcePatient.record.id;
  }

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IPatientFileJobData[]> {
    const patientFiles$ = from(
      groupImagesByPatient(
        migration.configuration.projectId,
        getBucketStoragePath(migration.source),
        `files/`
      )
    );

    const documentTag$ = combineLatest(
      migration.configuration.practices.map((practice) =>
        firstResult(
          Practice.mediaTagCol(practice),
          where('name', '==', 'Letter')
        )
      )
    ).pipe(
      map(compact),
      map((mediaTags) =>
        first(mediaTags.filter((tag) => tag.name === 'Letter'))
      ),
      errorNil('Letter tag not found')
    );

    return snapshotCombineLatest([
      patientFiles$,
      from(this.customMappings.fileCategories.getRecords(translationMap)),
      documentTag$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(([sourcePatients, [patientFiles, fileCategories, documentTag]]) =>
        sourcePatients.map((sourcePatient) => {
          let patientId = sourcePatient.data.patient_id.toString();
          const alternatePatientId = sourcePatient.data.patients_cart_num;

          if (alternatePatientId && patientId !== alternatePatientId) {
            patientId = alternatePatientId ?? patientId;
          }

          const files = Object.entries(patientFiles).find(([key]) => {
            if (!key.includes(patientId)) {
              return false;
            }

            const hasPatientName = this._hasPatientName(key, sourcePatient);

            if (!hasPatientName) {
              return false;
            }

            return true;
          });

          return {
            sourcePatient,
            fileCategories,
            documentTag,
            files: files ? files[1] : [],
          };
        })
      )
    );
  }

  getDestinationEntityRecordUid(data: IPatientFileJobData): string {
    return data.sourcePatient.record.uid;
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IPatientFileJobData
  ): Promise<
    | IPatientFileMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    try {
      const sourcePatientId = data.sourcePatient.data.patient_id.toString();
      const documents =
        await this.sourceEntities.patientDocuments.filterRecords(
          migration,
          'patientId',
          sourcePatientId
        );

      const patientRef = await translationMap.getDestination<IPatient>(
        sourcePatientId,
        PATIENT_RESOURCE_TYPE
      );

      if (!patientRef) {
        return this._buildErrorResponse(
          data.sourcePatient,
          `Couldn't resolve patient`
        );
      }

      return {
        sourcePatientId,
        patientRef,
        documents,
        files: data.files,
        fileCategories: data.fileCategories,
      };
    } catch (error) {
      return this._buildErrorResponse(
        data.sourcePatient,
        `Failed to build file data: ${getError(error)}`
      );
    }
  }

  hasMergeConflict(
    _translationMap: TranslationMapHandler,
    _data: IPatientFileMigrationData
  ): IPatientFileMigrationData | undefined {
    return;
  }

  buildMergeConflictRecord(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    jobData: IPatientFileJobData,
    _migrationData: IPatientFileMigrationData
  ): IDestinationEntityRecord & MergeConflictDestinationEntityRecord {
    return {
      uid: jobData.sourcePatient.record.uid,
      label: jobData.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.MergeConflict,
      sourceRef: jobData.sourcePatient.record.id,
    };
  }

  async runJob(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMapHandler: TranslationMapHandler,
    data: IPatientFileJobData,
    migrationData: IPatientFileMigrationData
  ): Promise<IDestinationEntityRecord> {
    try {
      const fileRefs = await this._addMediaToPatient(
        data,
        migrationData.sourcePatientId,
        migrationData.patientRef,
        migrationData.documents,
        migrationData.files,
        migrationData.fileCategories,
        translationMapHandler,
        migration.source,
        migration.configuration.projectId,
        migration.configuration.destinationBucket
      );
      return this._buildSuccessResponse(data.sourcePatient, fileRefs);
    } catch (error) {
      return this._buildErrorResponse(data.sourcePatient, getError(error));
    }
  }

  buildSkippedResponse(
    data: IPatientFileJobData
  ): IDestinationEntityRecord & SkippedDestinationEntityRecord {
    return {
      uid: data.sourcePatient.record.uid,
      label: data.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.Skipped,
      sourceRef: data.sourcePatient.record.id,
    };
  }

  private _hasPatientName(
    key: string,
    sourcePatient: IGetRecordResponse<D4WPatient>
  ): boolean {
    const keyLower = key.toLowerCase();
    if (!keyLower.includes(sourcePatient.data.surname.toLowerCase())) {
      return false;
    }

    if (keyLower.includes(sourcePatient.data.firstname.toLowerCase())) {
      return true;
    }

    if (
      sourcePatient.data.preferred_name &&
      keyLower.includes(sourcePatient.data.preferred_name.toLowerCase())
    ) {
      return true;
    }

    // eslint-disable-next-line no-console
    console.warn(
      `Patient name mismatch: ${key} - ${sourcePatient.data.firstname} ${sourcePatient.data.surname}`
    );
    return false;
  }

  private _buildSuccessResponse(
    sourcePatient: IGetRecordResponse<D4WPatient>,
    fileRefs: DocumentReference<IMedia>[]
  ): IDestinationEntityRecord<IPatientFileDestinationRecord> {
    return {
      uid: sourcePatient.record.uid,
      label: sourcePatient.record.label,
      data: {
        fileRefs,
      },
      status: DestinationEntityRecordStatus.Migrated,
      sourceRef: sourcePatient.record.id,
      migratedAt: toTimestamp(),
    };
  }

  private _buildErrorResponse(
    sourcePatient: IGetRecordResponse<D4WPatient>,
    errorMessage: string
  ): IDestinationEntityRecord & FailedDestinationEntityRecord {
    return {
      uid: sourcePatient.record.uid,
      label: sourcePatient.record.label,
      status: DestinationEntityRecordStatus.Failed,
      sourceRef: sourcePatient.record.id,
      errorMessage,
      failData: {
        patientRef: sourcePatient.record.id,
      },
    };
  }

  private async _addMediaToPatient(
    data: IPatientFileJobData,
    sourcePatientId: string,
    patientRef: DocumentReference<IPatient>,
    documents: IGetRecordResponse<D4WPatientDocument>[],
    files: IFileSummary[],
    fileCategories: WithPrimaryKey<ITranslationMap<ITag>>[],
    translationMap: TranslationMapHandler,
    source: ISource,
    projectId: string,
    destinationBucket: string
  ): Promise<DocumentReference<IMedia>[]> {
    // eslint-disable-next-line no-console
    console.log(
      `Found ${files.length} files for patient ${sourcePatientId} in the bucket`
    );

    const folderPath = files[0]?.folderPath;

    if (!files.length || !folderPath) {
      return [];
    }

    const copyFolderResponse = await this._copySourceFilesToDestination(
      getBucketStoragePath(source),
      `${folderPath}/`,
      patientRef,
      projectId,
      destinationBucket
    );

    const fileRefs = await resolveSequentially(
      copyFolderResponse.storageFiles,
      async (file) => {
        const fileDestinationRef = await translationMap.getDestination(
          file.path,
          PATIENT_FILE_RESOURCE_TYPE
        );

        const createdAt = getBucketFileCreatedAt(file);

        const fileName = file.path.split('/').pop();
        const matchingFile = files.find(
          (fileSummary) => fileSummary.fileName === fileName
        );

        const fileTags: INamedDocument<ITag>[] = [];

        if (matchingFile?.categoryName) {
          const category = fileCategories.find(
            (fileCategory) =>
              fileCategory.sourceIdentifier === matchingFile.categoryName
          );

          if (category?.destinationIdentifier) {
            fileTags.push({
              name: category.sourceIdentifier,
              ref: category.destinationIdentifier,
            });
          }
        }

        const mediaRef = await FirestoreMigrate.upsertDoc(
          Patient.mediaCol({ ref: patientRef }),
          {
            ...Media.init({
              name: fileName,
              path: file.path,
              tags: fileTags,
            }),
            createdAt,
          },
          fileDestinationRef?.id
        );

        if (!fileDestinationRef) {
          await translationMap.upsert({
            sourceIdentifier: file.path,
            destinationIdentifier: mediaRef,
            resourceType: PATIENT_FILE_RESOURCE_TYPE,
          });
        }

        return mediaRef;
      }
    );

    const documentRefs = compact(
      await resolveSequentially(documents, async (document) => {
        try {
          const path = await this._copySourceDocumentToDestination(
            getBucketStoragePath(source),
            `letters/${document.data.filename}`,
            patientRef,
            projectId,
            destinationBucket
          );

          if (!path) {
            throw new Error(
              `Couldn't copy document ${document.data.filename} to destination bucket`
            );
          }

          const documentDestinationRef = await translationMap.getDestination(
            document.record.uid,
            PATIENT_DOCUMENT_RESOURCE_TYPE
          );

          const mediaRef = await FirestoreMigrate.upsertDoc(
            Patient.mediaCol({ ref: patientRef }),
            {
              ...Media.init({
                name: document.data.filename,
                path,
                tags: [toNamedDocument(data.documentTag)],
              }),
              createdAt: document.record.translations.createdAt,
            },
            documentDestinationRef?.id
          );

          if (!documentDestinationRef) {
            await translationMap.upsert({
              sourceIdentifier: document.record.uid,
              destinationIdentifier: mediaRef,
              resourceType: PATIENT_DOCUMENT_RESOURCE_TYPE,
            });
          }

          return mediaRef;
        } catch (error) {
          // D4W letters can be deleted from the filesystem so this in most cases isn't an error
          // eslint-disable-next-line no-console
          console.error(
            `Error adding document ${document.data.filename} to patient ${sourcePatientId}`,
            error
          );
        }
      })
    );

    return [...fileRefs, ...documentRefs];
  }

  private async _copySourceFilesToDestination(
    sourceBucket: string,
    sourcePath: string,
    patientRef: DocumentReference<IPatient>,
    projectId: string,
    destinationBucket: string
  ): Promise<IStorageSyncResponse> {
    const copyStorage = new CopyFiles(
      sourceBucket,
      sourcePath,
      destinationBucket,
      Patient.storagePath({ ref: patientRef }),
      projectId
    );

    return {
      storageFiles: await copyStorage.copyAll(
        [/.*.db/],
        [CLOUD_STORAGE_CREATED_AT_METADATA_KEY],
        true
      ),
    };
  }

  private async _copySourceDocumentToDestination(
    sourceBucket: string,
    sourcePath: string,
    patientRef: DocumentReference<IPatient>,
    projectId: string,
    destinationBucket: string
  ): Promise<string> {
    const copyStorage = new CopyFile(
      sourceBucket,
      sourcePath,
      destinationBucket,
      Patient.storagePath({ ref: patientRef }),
      projectId
    );

    return copyStorage.copy();
  }
}

export async function groupImagesByPatient(
  projectId: string,
  bucketName: string,
  bucketPath: string
): Promise<Record<string, IFileSummary[]>> {
  const storage = new CopyFiles(bucketName, bucketPath, '', '', projectId)
    .storage;

  const [files] = await storage.bucket(bucketName).getFiles({
    prefix: bucketPath,
  });

  const missingFields: string[] = [];

  const bucketFiles = uniq(
    compact(
      files.map((file) => {
        const folderPath = file.name.split('/');

        const fileSummary: IFileSummary = {
          patientName: '',
          patientId: '',
          fileName: '',
          folderPath: '',
          categoryName: undefined,
        };

        if (folderPath.length > 2) {
          const patientIdMatch = new RegExp(/\[([0-9A-Za-z\s]+)\]$/).exec(
            folderPath[1]
          );
          fileSummary.patientId = patientIdMatch?.[1] ?? '';
          const patientNameMatch = new RegExp(/^([\w'\-\s]+,[\w'\-\s]+)/).exec(
            folderPath[1]
          );
          fileSummary.patientName = (patientNameMatch?.[1] ?? '')
            .split(',')
            .reverse()
            .join(' ')
            .trim();
        }

        if (folderPath.length > 3) {
          fileSummary.categoryName = folderPath[2].trim();
        }

        fileSummary.folderPath = folderPath.slice(0, 2).join('/') ?? '';
        fileSummary.fileName = folderPath.slice(2).join('_');

        if (
          !fileSummary.patientId ||
          !fileSummary.patientName ||
          !fileSummary.fileName
        ) {
          missingFields.push(file.name);
        }

        return fileSummary;
      })
    )
  );

  return groupBy(
    bucketFiles,
    (summary) => `${summary.patientId}-${summary.patientName}`
  );
}
